import React from 'react';
import { useDispatch } from 'react-redux';

import BankVerificationMethod from './BankVerificationMethod';
import {
  DOCUMENT_DEFINITIONS,
  FinancialVerificationProvider,
  useFinancialVerification,
} from './FinancialVerificationContext';
import OtherFinancialDocuments from './OtherFinancialDocuments';
import { uploadCompanyDocuments } from '../../../../actions/companies';
import { asyncActions } from '../../../../actions/general';
import { setAccessToken } from '../../../../actions/plaid';
import { getRoute } from '../../../../actions/sessions';
import useSessionEntity from '../../../../hooks/useSessionEntity';
import PrimaryButton from '../../../shared/buttons/PrimaryButton';

const documentTypeMap = {
  tax_exempt: 'tax_document',
  financial_statements_1: 'financial_statements',
  financial_statements_2: 'financial_statements',
  ytd_balance_sheet: 'financial_statements',
  ytd_profit_loss: 'financial_statements',
};

function FinancialVerificationContent() {
  const dispatch = useDispatch();
  const company = useSessionEntity('company');
  const {
    isAnyActionCompleted,
    uploadedDocuments,
    bankStatementsFiles,
    plaidConnectionData,
    isUploading,
    uploadError,
    setIsUploading,
    setUploadError,
    financialDocuments,
  } = useFinancialVerification();

  const handleComplete = async () => {
    if (!company?.id) {
      console.error('No company ID available');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const actions = [];

      const files = [];
      const fileTypes = [];
      const fileDescriptions = [];

      if (financialDocuments && financialDocuments.length > 0) {
        Object.entries(uploadedDocuments).forEach(([docId, file]) => {
          if (file) {
            const docType = documentTypeMap[docId];
            const doc = financialDocuments.find((d) => d.id === docId);
            const description = doc?.title || docType;

            files.push(file);
            fileTypes.push(docType);
            fileDescriptions.push(description);
          }
        });
      }

      bankStatementsFiles?.forEach((file) => {
        const docType = 'bank_statements';
        const description = DOCUMENT_DEFINITIONS[docType].title;

        files.push(file);
        fileTypes.push(docType);
        fileDescriptions.push(description);
      });

      if (files.length > 0) {
        actions.push(
          uploadCompanyDocuments(company.id, files, fileTypes, fileDescriptions)
        );
      }

      if (plaidConnectionData?.publicToken) {
        actions.push(setAccessToken(plaidConnectionData.publicToken));
      }

      actions.push(getRoute());
      await dispatch(asyncActions(...actions));
    } catch (error) {
      console.error('Error in handleComplete:', error);
      setUploadError('Failed to complete verification. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <BankVerificationMethod />
      <OtherFinancialDocuments />
      <div className="flex flex-col items-start gap-4">
        {uploadError && (
          <div className="text-red-600 text-sm">{uploadError}</div>
        )}
        <PrimaryButton
          onClick={handleComplete}
          buttonText={isUploading ? 'Loading...' : 'Complete'}
          ariaLabel="Complete"
          buttonColor="bg-primary-blue"
          hoverClass="hover:bg-primary-blue-hover"
          disabled={!isAnyActionCompleted || isUploading}
          isLoading={isUploading}
        />
      </div>
    </div>
  );
}

export default function FinancialVerification() {
  const application = useSessionEntity('application');
  const businessType = application?.business_type || '';
  const tclAmount = Number(
    application?.trade_credit_amount_requested.replace(/,/g, '')
  );

  return (
    <FinancialVerificationProvider
      tclAmount={tclAmount}
      businessType={businessType}
    >
      <FinancialVerificationContent />
    </FinancialVerificationProvider>
  );
}
